// Travel estimates without a routing API: straight-line distance, a typical
// road detour factor and an average speed. Always shown as approximate ("~",
// "about"), never as an exact travel time.

export type LatLon = { lat: number; lon: number }

const EARTH_KM = 6371
export const DETOUR_FACTOR = 1.3
const WALK_KMH = 4.5
const DRIVE_KMH = 30
const WALK_MAX_ROAD_KM = 1.5
// Beyond this a city-traffic speed is meaningless (flights, intercity trips).
const MAX_ESTIMATE_ROAD_KM = 150
const KM_PER_MI = 1.609344

export function haversineKm(a: LatLon, b: LatLon): number {
  const rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(b.lat - a.lat)
  const dLon = rad(b.lon - a.lon)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

export type TravelEstimate = {
  // Straight-line distance, km.
  km: number
  mode: 'walk' | 'drive' | null
  // Rounded to 5 min; 0 means "under 5 min"; null when too far to estimate.
  minutes: number | null
}

export function estimateTravel(from: LatLon, to: LatLon): TravelEstimate {
  const km = haversineKm(from, to)
  const road = km * DETOUR_FACTOR
  if (road > MAX_ESTIMATE_ROAD_KM) return { km, mode: null, minutes: null }
  const mode = road < WALK_MAX_ROAD_KM ? 'walk' : 'drive'
  const raw = (road / (mode === 'walk' ? WALK_KMH : DRIVE_KMH)) * 60
  return { km, mode, minutes: raw < 5 ? 0 : Math.round(raw / 5) * 5 }
}

// Friendly distance in km or mi: one decimal under 10, whole numbers under 100,
// then tens / hundreds.
export function friendlyDistance(
  km: number,
  imperial = false,
): { value: number; unit: 'km' | 'mi' } {
  const d = imperial ? km / KM_PER_MI : km
  const value =
    d < 10
      ? Math.max(0.1, Math.round(d * 10) / 10)
      : d < 100
        ? Math.round(d)
        : d < 1000
          ? Math.round(d / 10) * 10
          : Math.round(d / 100) * 100
  return { value, unit: imperial ? 'mi' : 'km' }
}

// en-US speaks miles; everyone else gets km.
export const usesMiles = (locale: string | undefined) => /^en-(US|LR|MM)$/i.test(locale ?? '')

// Plain English for the coach prompt: "~6 km away, about 15 min by car".
export function travelPhrase(t: TravelEstimate): string {
  const { value, unit } = friendlyDistance(t.km)
  const dist = `~${value} ${unit} away`
  if (t.minutes == null || !t.mode) return dist
  const time = t.minutes === 0 ? 'under 5 min' : `about ${t.minutes} min`
  return `${dist}, ${time} ${t.mode === 'walk' ? 'on foot' : 'by car'}`
}
